import { Flex, Text } from '@chakra-ui/react'
import React from 'react'

import { type ReportingApi, ReportingApiProvider } from '../../api'
import {
  ReportingI18nProvider,
  type ReportingTranslationLike,
  useReportingI18n,
} from '../../i18n'
import { QueueSelector } from './components/QueueSelector'
import { useQueues } from './hooks/useQueues'
import type { QueueDashboardSettings } from './types'

export interface QueueDashboardFiltersProps {
  api: ReportingApi
  t?: ReportingTranslationLike
  locale?: string
  selectedPbxId: number
  selectedQueueId: string | null
  onQueueChange: (queueId: string | null) => void
  settings: QueueDashboardSettings
  onSettingsChange: (settings: Partial<QueueDashboardSettings>) => void
}

type QueueDashboardFiltersInnerProps = Omit<
  QueueDashboardFiltersProps,
  'api' | 't' | 'locale'
>

export const QueueDashboardFiltersInner: React.FC<
  QueueDashboardFiltersInnerProps
> = ({
  selectedPbxId,
  selectedQueueId,
  onQueueChange,
  settings: _settings,
  onSettingsChange: _onSettingsChange,
}) => {
  const { text } = useReportingI18n()
  const {
    data: queues = [],
    isLoading: isLoadingQueues,
    error: _queuesError,
  } = useQueues({
    pbxId: selectedPbxId ?? 0,
    options: {
      enabled: !!selectedPbxId,
    },
  })

  return (
    <Flex
      width="100%"
      height="72px"
      padding="0 20px"
      justifyContent="space-between"
      alignItems="center"
      alignSelf="stretch"
      borderBottom="1px solid var(--chakra-colors-border-default)"
      background="var(--chakra-colors-bg-default)"
      data-testid="queue-dashboard-filters"
    >
      <Flex direction="column" gap="2px">
        <Text
          fontSize="1.25rem"
          fontWeight="700"
          lineHeight="1.5rem"
          color="var(--chakra-colors-fg-default)"
        >
          {text('queueDashboard.filters.title', 'Queue')}
        </Text>
        <Text
          fontSize="0.85rem"
          fontWeight="400"
          lineHeight="1rem"
          color="var(--chakra-colors-fg-muted)"
        >
          {text(
            'queueDashboard.filters.subtitle',
            "See how your team's call activity compares",
          )}
        </Text>
      </Flex>

      <QueueSelector
        queues={queues}
        selectedQueueId={selectedQueueId}
        onQueueChange={onQueueChange}
        isLoading={isLoadingQueues}
      />
    </Flex>
  )
}

export const QueueDashboardFilters: React.FC<QueueDashboardFiltersProps> = ({
  api,
  t,
  locale,
  ...rest
}) => (
  <ReportingApiProvider api={api}>
    <ReportingI18nProvider t={t} locale={locale}>
      <QueueDashboardFiltersInner {...rest} />
    </ReportingI18nProvider>
  </ReportingApiProvider>
)

export default QueueDashboardFilters
